import React, { FC, useEffect, useState } from 'react';
import { AlertColor, Box, Button, Modal, TextField, Tooltip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import AccessGuard from '../../components/AccessGuard';
import TabPanel from '../../components/TabPanel';
import { USER_GROUP_ADMIN } from '../../types/User';
import ApiService from '../../ApiService';
import Column from '../../components/Column';
import Row from '../../components/Row';
import { ITab } from './DspAdGroupDictsTab';
import CDGrid, { CustomPagination } from '../../components/CDGrid';
import {
    GridColDef,
    GridFooterContainer,
    GridPaginationModel,
    GridRenderEditCellParams,
    GridSortModel,
} from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { InfoMessage, InfoMessageProps } from '../../components/InfoMessage';
import { AxiosPageableResponse } from '../../types/AxiosPageableResponse';
import BrandAutocomplete from '../../components/Form/Fields/BrandAutocomplete';
import { Brand } from '../../types/Brand';
import { Oem } from '../../types/Oem';

function DataGridFooter(props: { totalElements: number; numberOfElements: number }) {
    return (
        <GridFooterContainer>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '10px' }}>
                <CustomPagination />
                <span>
                    Showing {props.numberOfElements} of {props.totalElements} records
                </span>
            </div>
        </GridFooterContainer>
    );
}

const BrandAutocompleteEditInputCell: FC<GridRenderEditCellParams> = (params: GridRenderEditCellParams) => {
    return (
        <FormControl fullWidth variant="outlined" size="small" required>
            <BrandAutocomplete
                multiple
                required
                label="Brands"
                value={params.value.map((v: Brand) => v.id)}
                onChange={(value: any) => {
                    params.api.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: value,
                    });
                }}
            />
        </FormControl>
    );
};

const OemsTab = ({ tab, activeTab }: { tab: ITab; activeTab: number }) => {
    const defaultPageSize: number = 50;
    const pageValueArray = [10, 20, 50, 75, 100];

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: defaultPageSize,
    });

    const [totalElements, setTotalElements] = useState(0);
    const [numberOfElements, setNumberOfElements] = useState(0);
    const [list, setList] = useState<Oem[] | null>(null);
    const [open, setOpen] = useState(false);
    const [infoMessage, setInfoMessage] = useState<InfoMessageProps>({
        message: null,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'id', sort: 'desc' }]);

    const [formData, setFormData] = useState<{
        name: string;
        brands: Brand[];
    }>({
        name: '',
        brands: [],
    });

    const handleFormChange = (field: keyof typeof formData, value: string | number | unknown) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const resetForm = () => {
        setFormData((prev) => ({
            ...prev,
            name: '',
            brands: [],
        }));
    };

    const handleChange = (event: SelectChangeEvent<string>) => {
        const value = parseInt(event.target.value, 10);
        const pageSize = pageValueArray.includes(value) ? value : defaultPageSize;

        setPaginationModel((prev) => ({
            ...prev,
            pageSize,
            page: 0, // reset to first page when page size changes
        }));
    };

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        ApiService.addOem({
            name: formData.name,
            brands: formData.brands,
        }).then((newItem) => {
            fetchData();
            showInfoMessage('success', 'OEM has been successfully created.');
        });
        handleClose();
    }

    function showInfoMessage(alertColor: AlertColor, message: string) {
        setInfoMessage({
            message: message,
            severity: alertColor,
            onClose: () => {
                setInfoMessage({ message: null });
            },
        });
    }

    const handleClose = () => {
        resetForm();
        setOpen(false);
    };

    const handleOpen = () => {
        resetForm();
        setOpen(true);
    };

    const editModalStyles = {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        borderRadius: '8px',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
    };

    const handlePaginationModelChange = (model: GridPaginationModel) => {
        setPaginationModel(model);
    };

    const fetchData = async () => {
        const { page, pageSize } = paginationModel;
        const response: AxiosPageableResponse<Oem> = await ApiService.getPaginatedOems(page, pageSize, sortModel);
        if (response.status === 200) {
            setList(response.data.content);
            setTotalElements(response.data.totalElements);
            setNumberOfElements(response.data.numberOfElements);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, sortModel]);

    const columns: GridColDef[] = [
        {
            flex: 1,
            minWidth: 100,
            field: 'id',
            headerName: 'ID',
            display: 'flex',
        },
        {
            flex: 2,
            minWidth: 100,
            field: 'name',
            headerName: 'OEM',
            editable: true,
            display: 'flex',
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <Row gap={0.5}>
                        <Typography variant="body2" fontWeight={500}>
                            OEM
                        </Typography>
                        <EditOutlinedIcon fontSize="small" />
                    </Row>
                </Tooltip>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'brands',
            headerName: 'Brands',
            sortable: false,
            editable: true,
            display: 'flex',
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <Row gap={0.5}>
                        <Typography variant="body2" fontWeight={500}>
                            Brands
                        </Typography>
                        <EditOutlinedIcon fontSize="small" />
                    </Row>
                </Tooltip>
            ),
            renderCell: (params) => {
                return params.value.map((v: Brand) => v.name).join(', ');
            },
            renderEditCell: (params: GridRenderEditCellParams) => <BrandAutocompleteEditInputCell {...params} />,
            preProcessEditCellProps: (params) => {
                return {
                    ...params.props,
                    error: params.props.value.length <= 0,
                };
            },
        },
    ];

    return (
        <AccessGuard accessGroup={USER_GROUP_ADMIN}>
            <TabPanel index={tab.id} value={activeTab} sx={{ p: 0 }}>
                <InfoMessage {...infoMessage} />
                <Column gap={2}>
                    <Row justifyContent="space-between" alignItems="center">
                        <Row gap={0} sx={{ width: '100%' }}>
                            <Typography component="span" color="text.primary" variant="subtitle2" fontWeight={500}>
                                Instruction:{' '}
                                <Typography
                                    component="span"
                                    color="text.secondary"
                                    variant="subtitle2"
                                    fontWeight={500}
                                >
                                    {tab.description}
                                </Typography>
                            </Typography>
                        </Row>
                        <Row flex={0} justifyContent="flex-end">
                            <Button variant="text" onClick={handleOpen} sx={{ marginTop: '-1%' }}>
                                Add OEM
                            </Button>
                            <FormControl sx={{ minWidth: 120 }} size="small">
                                <InputLabel id="rows-per-page-label">Rows Per Page</InputLabel>
                                <Select
                                    labelId="rows-per-page-label"
                                    id="rows-per-page"
                                    value={paginationModel.pageSize.toString()}
                                    label="Rows Per Page"
                                    onChange={handleChange}
                                >
                                    {pageValueArray.map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Row>
                    </Row>
                    <Modal
                        open={open}
                        onClose={handleClose}
                        aria-labelledby="modal-modal-title"
                        aria-describedby="modal-modal-description"
                    >
                        <Box sx={editModalStyles}>
                            <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ textAlign: 'center' }}>
                                Add OEM
                            </Typography>
                            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 2,
                                        mt: 2,
                                        width: '100%',
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        label="OEM"
                                        variant="outlined"
                                        size="small"
                                        required
                                        onChange={(e) => handleFormChange('name', e.target.value)}
                                    />
                                    <FormControl fullWidth variant="outlined" size="small" required>
                                        <BrandAutocomplete
                                            multiple
                                            required
                                            label="Brands"
                                            value={formData.brands.map((v: Brand) => v.id)}
                                            onChange={(value: any) => {
                                                handleFormChange('brands', value);
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                                    <Button
                                        variant="contained"
                                        sx={{ width: '120px' }}
                                        startIcon={<SaveIcon />}
                                        type="submit"
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        sx={{ width: '120px' }}
                                        onClick={handleClose}
                                        startIcon={<CloseIcon />}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </form>
                        </Box>
                    </Modal>
                    <CDGrid
                        id="oems"
                        variant="contained"
                        rowCount={totalElements}
                        columns={columns}
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={handlePaginationModelChange}
                        sortingMode="server"
                        onSortModelChange={setSortModel}
                        rows={list === null ? [] : list}
                        processRowUpdate={(newRow) => {
                            ApiService.updateOem(newRow);
                            setList((prevRows) =>
                                prevRows === null ? [] : prevRows.map((row) => (row.id === newRow.id ? newRow : row))
                            );
                            return newRow;
                        }}
                        onProcessRowUpdateError={() => {}}
                        slots={{
                            footer: () => (
                                <DataGridFooter totalElements={totalElements} numberOfElements={numberOfElements} />
                            ),
                        }}
                    />
                </Column>
            </TabPanel>
        </AccessGuard>
    );
};

export default OemsTab;
